import { Component, OnDestroy, OnInit } from '@angular/core';
import { DecimalPipe, NgClass } from '@angular/common';
import { Subscription } from 'rxjs';

import { PageBreadcrumbComponent } from '../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../shared/components/ui/badge/badge.component';
import { ButtonComponent } from '../../shared/components/ui/button/button.component';

import { CreditDebitNoteService } from '../../services/credit-debit-note.service';
import { NotificationService } from '../../shared/components/ui/notification/notification.service';
import { CreditDebitNoteResponse } from '../../dto/credit-debit-note.response';
import { CreditDebitNoteRegisterComponent } from './credit-debit-note-register.component';

@Component({
    selector: 'app-credit-debit-note',
    standalone: true,
    imports: [
        PageBreadcrumbComponent,
        BadgeComponent,
        ButtonComponent,
        DecimalPipe,
        NgClass,
        CreditDebitNoteRegisterComponent,
    ],
    templateUrl: './credit-debit-note.component.html',
    styleUrl: './credit-debit-note.component.css',
})
export class CreditDebitNoteComponent implements OnInit, OnDestroy {

    // =========================
    // UI STATE
    // =========================
    loadingTable = false;
    showForm = false;

    // =========================
    // DATA
    // =========================
    notes: CreditDebitNoteResponse[] = [];
    selectedNote?: CreditDebitNoteResponse;

    // =========================
    // PAGINATION
    // =========================
    currentPage = 1;
    itemsPerPage = 5;
    readonly pageSizeOptions = [5, 10, 15, 20, 50];

    // =========================
    // SORT
    // =========================
    sortColumn = '';
    sortDir: 'asc' | 'desc' = 'asc';

    // =========================
    // SEARCH
    // =========================
    searchTerm = '';

    // =========================
    // TOOLTIP
    // =========================
    tooltipVisible = false;
    tooltipCode = '';
    tooltipText = '';
    tooltipX = 0;
    tooltipY = 0;

    showTooltip(event: MouseEvent, code: number | undefined, message: string | undefined): void {
        if (!message) return;
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        this.tooltipCode = code != null ? String(code) : '';
        this.tooltipText = message;
        this.tooltipX = rect.left;
        this.tooltipY = rect.top;
        this.tooltipVisible = true;
    }

    hideTooltip(): void {
        this.tooltipVisible = false;
    }

    // =========================
    // SUBSCRIPTIONS
    // =========================
    private sub = new Subscription();

    constructor(
        private noteService: CreditDebitNoteService,
        private notify: NotificationService
    ) { }

    // =========================
    // INIT / DESTROY
    // =========================
    ngOnInit(): void {
        this.loadNotes();
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    // =========================
    // LOAD
    // =========================
    loadNotes(): void {
        this.loadingTable = true;
        const s = this.noteService.getAll().subscribe({
            next: res => {
                this.notes = res?.data ?? [];
                this.loadingTable = false;
            },
            error: err => {
                this.notes = [];
                this.loadingTable = false;
                this.notify.error(err?.error?.message ?? 'Error al listar notas de crédito/débito');
            }
        });
        this.sub.add(s);
    }

    // =========================
    // CREATE / VIEW
    // =========================
    onCreateNote(): void {
        this.selectedNote = undefined;
        this.showForm = true;
    }

    onViewNote(note: CreditDebitNoteResponse): void {
        this.selectedNote = note;
        this.showForm = true;
    }

    handleNoteSaved(): void {
        this.showForm = false;
        this.selectedNote = undefined;
        this.loadNotes();
    }

    onCloseForm(): void {
        this.showForm = false;
        this.selectedNote = undefined;
    }

    // =========================
    // RESEND
    // =========================
    onResend(note: CreditDebitNoteResponse): void {
        this.noteService.resend(note.id).subscribe({
            next: () => {
                this.notify.success('Nota reenviada a SUNAT correctamente');
                this.loadNotes();
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al reenviar nota');
            }
        });
    }

    // =========================
    // SEARCH
    // =========================
    setSearchTerm(value: string): void {
        this.searchTerm = (value ?? '').trim().toLowerCase();
        this.currentPage = 1;
    }

    get filteredNotes(): CreditDebitNoteResponse[] {
        let list = this.notes;
        if (this.searchTerm) {
            list = list.filter(n =>
                n.creditDebitNoteType?.name?.toLowerCase().includes(this.searchTerm) ||
                n.originalDocument?.series?.toLowerCase().includes(this.searchTerm) ||
                n.originalDocument?.sequence?.toLowerCase().includes(this.searchTerm) ||
                n.series?.toLowerCase().includes(this.searchTerm) ||
                n.sequence?.toLowerCase().includes(this.searchTerm) ||
                n.sale?.client?.businessName?.toLowerCase().includes(this.searchTerm) ||
                n.sale?.client?.firstName?.toLowerCase().includes(this.searchTerm) ||
                n.sale?.client?.lastName?.toLowerCase().includes(this.searchTerm)
            );
        }
        if (this.sortColumn === 'cliente') {
            list = [...list].sort((a, b) => {
                const va = a.sale?.client?.businessName || `${a.sale?.client?.firstName ?? ''} ${a.sale?.client?.lastName ?? ''}`.trim();
                const vb = b.sale?.client?.businessName || `${b.sale?.client?.firstName ?? ''} ${b.sale?.client?.lastName ?? ''}`.trim();
                return this.sortDir === 'asc' ? va.localeCompare(vb, 'es') : vb.localeCompare(va, 'es');
            });
        }
        return list;
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredNotes.length / this.itemsPerPage));
    }

    get pageItems(): (number | '...')[] {
        const total = this.totalPages;
        const current = this.currentPage;
        const pages = new Set<number>();
        for (let i = 1; i <= Math.min(3, total); i++) pages.add(i);
        for (let i = Math.max(1, total - 2); i <= total; i++) pages.add(i);
        for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
        const sorted = Array.from(pages).sort((a, b) => a - b);
        const result: (number | '...')[] = [];
        for (let i = 0; i < sorted.length; i++) {
            if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
            result.push(sorted[i]);
        }
        return result;
    }

    get currentItems(): CreditDebitNoteResponse[] {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filteredNotes.slice(start, start + this.itemsPerPage);
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
        }
    }

    toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDir = 'asc';
        }
    }

    onPageSizeChange(size: number): void {
        this.itemsPerPage = size;
        this.currentPage = 1;
    }

    // =========================
    // HELPERS
    // =========================
    getClientName(note: CreditDebitNoteResponse): string {
        const client = note.sale?.client;
        if (!client) return '-';
        if (client.businessName) return client.businessName;
        return `${client.firstName} ${client.lastName}`.trim();
    }

    getOriginalDoc(note: CreditDebitNoteResponse): string {
        if (!note.originalDocument) return '-';
        return `${note.originalDocument.series}-${note.originalDocument.sequence}`;
    }

    getNoteDoc(note: CreditDebitNoteResponse): string {
        if (!note.series || !note.sequence) return 'Sin emitir';
        return `${note.series}-${note.sequence}`;
    }

    formatDate(dateStr?: string): string {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }

    getStatusBadgeColor(status: string): 'success' | 'error' | 'warning' {
        if (status === 'ACEPTADO') return 'success';
        if (status === 'PENDIENTE') return 'warning';
        return 'error';
    }

    getNoteTypeBadgeColor(category: string): 'success' | 'error' | 'warning' {
        return category === 'CREDITO' ? 'success' : 'error';
    }

    openFile(url: string): void {
        window.open(url, '_blank');
    }

    canResend(note: CreditDebitNoteResponse): boolean {
        return note.status === 'RECHAZADO' || note.status === 'ERROR';
    }
}
